import { createContext, useContext } from "react";

/**
 * A per-render registry that lets <SeoMetaTags> publish the page's metadata to
 * two consumers without touching the DOM:
 *
 *  - <SeoPageLayout>, which renders the visible FAQ block from the same FAQPage
 *    schema the head uses (so visible FAQs and JSON-LD can never disagree), and
 *  - the SSR entry, which turns the collected metadata into real <head> tags.
 *
 * Entries are keyed by pathname so client-side navigation never reads a stale
 * page's data. The registry is a plain mutable object rather than React state
 * because it must be readable during the same render pass that populates it.
 */
export const SeoRegistryContext = createContext(null);

export function createSeoRegistry() {
  const entries = new Map();
  return {
    set(pathname, data) {
      entries.set(pathname, data);
    },
    get(pathname) {
      return entries.get(pathname) || null;
    },
    latest() {
      let last = null;
      for (const value of entries.values()) last = value;
      return last;
    },
  };
}

export function useSeoRegistry() {
  return useContext(SeoRegistryContext);
}

export function extractFaqEntries(structuredData) {
  const items = Array.isArray(structuredData) ? structuredData.flat() : [structuredData];
  const entries = [];
  const seen = new Set();
  items.forEach((item) => {
    if (!item || typeof item !== "object" || item["@type"] !== "FAQPage") return;
    if (!Array.isArray(item.mainEntity)) return;
    item.mainEntity.forEach((entity) => {
      const question = String(entity?.name || "").trim();
      const answer = String(entity?.acceptedAnswer?.text || "").trim();
      if (!question || !answer) return;
      const key = `${question.toLowerCase()}::${answer.toLowerCase()}`;
      if (seen.has(key)) return;
      seen.add(key);
      entries.push({ question, answer });
    });
  });
  return entries;
}
